// src/components/quality/CreateCustomerAssessmentForm.js
import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toBackendFormat } from '../../hooks/useDateTimeFilter';
import useAuth from "../../hooks/useAuth";

const CreateCustomerAssessmentForm = ({ projectId, onClose, onAssessmentCreated }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    call_datetime: '',
    phone_number: '',
    operator_id: '',
    operator_name: '',
    agent_name: '',
    customer_question: '',
    operator_response: '',
    error_tags: [],
    internal_comment: '',
    needs_os: false,
    comment_for_send: '',
    assessor_name: '',
    assessment_date: (() => {
      const now = new Date();
      // Конвертируем текущее время в UTC+3 для отображения
      // const localNow = new Date(now.getTime() - 3 * 60 * 60 * 1000);
      const localNow = new Date(now.getTime());
      const year = localNow.getFullYear();
      const month = String(localNow.getMonth() + 1).padStart(2, '0');
      const day = String(localNow.getDate()).padStart(2, '0');
      const hours = String(localNow.getHours()).padStart(2, '0');
      const minutes = String(localNow.getMinutes()).padStart(2, '0');
      const seconds = String(localNow.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    })(),
    theme: '',
    subtheme: ''
  });
  
  // Состояние для тегов
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  // Состояние для операторов
  const [fieldOptions, setFieldOptions] = useState({
    operators: []
  });

  // Состояние для тем и подтем
  const [themes, setThemes] = useState([]);
  const [subthemes, setSubthemes] = useState([]);
  const [loadingThemes, setLoadingThemes] = useState(false);

  const [projectName, setProjectName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Загрузка данных проекта по API
  useEffect(() => {
    if (!projectId) return;

    const fetchData = async () => {
      try {
        // Получение операторов
        const operatorsResponse = await api.get(`/operators?project_id=${projectId}`);
        const operatorsData = operatorsResponse.data.operators;
        const operators = Array.isArray(operatorsData) ? operatorsData : [];

        setFieldOptions(prev => ({
          ...prev,
          operators: operators
        }));

        // Если есть только один оператор, подставляем его
        if (operators.length === 1) {
          const operator = operators[0];
          setFormData(prev => ({
            ...prev,
            operator_id: operator.id,
            operator_name: `${operator.surname} ${operator.name}`,
            agent_name: `${operator.surname} ${operator.name}`
          }));
        }
        
        setProjectName('Проект'); // Имя проекта не возвращается, оставляем заглушку
      } catch (err) {
        console.error('Ошибка загрузки данных:', err);
        setError('Не удалось загрузить данные. Проверьте подключение или обратитесь к администратору.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);
  
  // Загрузка тегов для проекта
  useEffect(() => {
    if (!projectId) return;
    
    const fetchTags = async () => {
      setTagsLoading(true);
      try {
        const response = await api.get(`/quality-assessment-customer/tags?project_id=${projectId}`);
        setAvailableTags(response.data.tags || []);
      } catch (err) {
        console.error('Ошибка загрузки тегов:', err);
        setError('Не удалось загрузить теги для выбора');
      } finally {
        setTagsLoading(false);
      }
    };
    
    fetchTags();
  }, [projectId]);
  
  // Загрузка тем для проекта
  useEffect(() => {
    if (!projectId) return;
    
    const fetchThemes = async () => {
      setLoadingThemes(true);
      try {
        const response = await api.get(`/assessments-customer-themes/${projectId}`);
        const themesData = response.data.themes || [];
        setThemes(themesData);
        
        // Если есть только одна тема, подставляем её
        if (themesData.length === 1) {
          setFormData(prev => ({
            ...prev,
            theme: themesData[0].name
          }));
          // Загружаем подтемы для этой темы
          setSubthemes(themesData[0].subthemes || []);
        }
      } catch (err) {
        console.error('Ошибка загрузки тем:', err);
        setError('Не удалось загрузить темы для выбора');
      } finally {
        setLoadingThemes(false);
      }
    };
    
    fetchThemes();
  }, [projectId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };
  
  // Обработчик выбора темы
  const handleThemeChange = (e) => {
    const themeName = e.target.value;
    const selectedTheme = themes.find(t => t.name === themeName);
    
    setFormData(prev => ({
      ...prev,
      theme: themeName,
      subtheme: '' // Сбрасываем подтему при смене темы
    }));
    
    // Устанавливаем подтемы для выбранной темы
    if (selectedTheme) {
      setSubthemes(selectedTheme.subthemes || []);
    } else {
      setSubthemes([]);
    }
  };
  
  // Обработчик выбора тега из выпадающего списка
  const handleTagSelect = (e) => {
    const tagId = parseInt(e.target.value, 10);
    if (isNaN(tagId)) return;
    
    const tag = availableTags.find(t => t.id === tagId);
    if (tag && !selectedTags.some(t => t.id === tag.id)) {
      setSelectedTags(prev => [...prev, tag]);
    }
    
    // Сбрасываем выбор в селекте
    e.target.value = '';
  };
  
  // Обработчик удаления тега
  const removeTag = (tagId) => {
    setSelectedTags(prev => prev.filter(t => t.id !== tagId));
  };

  const handleOperatorChange = (e) => {
    const selectedId = e.target.value;
    const selectedOperator = fieldOptions.operators.find(op => op.id.toString() === selectedId);
    if (selectedOperator) {
      setFormData(prev => ({
        ...prev,
        operator_id: selectedOperator.id,
        operator_name: `${selectedOperator.surname} ${selectedOperator.name}`,
        agent_name: `${selectedOperator.surname} ${selectedOperator.name}`
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Проверка обязательных полей
      if (!formData.call_datetime) {
        setError('Пожалуйста, заполните обязательное поле: Дата и время звонка');
        setSubmitting(false);
        return;
      }
      
      if (!formData.phone_number.trim()) {
        setError('Пожалуйста, заполните обязательное поле: Номер телефона');
        setSubmitting(false);
        return;
      }
      
      if (!formData.operator_id) {
        setError('Пожалуйста, заполните обязательное поле: Оператор');
        setSubmitting(false);
        return;
      }
      
      if (!formData.customer_question.trim()) {
        setError('Пожалуйста, заполните обязательное поле: Вопрос клиента');
        setSubmitting(false);
        return;
      }
      
      if (!formData.operator_response.trim()) {
        setError('Пожалуйста, заполните обязательное поле: Ответ оператора');
        setSubmitting(false);
        return;
      }
      
      if (!formData.assessor_name.trim()) {
        setError('Пожалуйста, заполните обязательное поле: ФИО проверяющего');
        setSubmitting(false);
        return;
      }

      // Проверка наличия хотя бы одного тега
      if (selectedTags.length === 0) {
        setError('Пожалуйста, выберите хотя бы один тег ошибки');
        setSubmitting(false);
        return;
      }
      
      // Подготовка данных для отправки
      const formatDateTime = (dateTimeStr) => {
        if (!dateTimeStr) return null;
        
        // Проверяем формат ДД.ММ.ГГГГ ЧЧ:ММ:СС
        const dateMatch = dateTimeStr.match(/(\d{2})\.(\d{2})\.(\d{4}) (\d{1,2}):(\d{2}):(\d{2})/);
        if (dateMatch) {
          const [, day, month, year, hours, minutes, seconds] = dateMatch;
          // Создаем дату в локальном времени
          const localDate = new Date(year, month - 1, day, hours, minutes, seconds);
          // Конвертируем в UTC, вычитая 3 часа (предполагаем, что введенное время в MSK)
          const utcDate = new Date(localDate.getTime() - 3 * 60 * 60 * 1000);
          // const utcDate = new Date(localDate.getTime());

          const utcYear = utcDate.getUTCFullYear();
          const utcMonth = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
          const utcDay = String(utcDate.getUTCDate()).padStart(2, '0');
          const utcHours = String(utcDate.getUTCHours()).padStart(2, '0');
          const utcMinutes = String(utcDate.getUTCMinutes()).padStart(2, '0');
          const utcSeconds = String(utcDate.getUTCSeconds()).padStart(2, '0');
          
          return `${utcYear}-${utcMonth}-${utcDay} ${utcHours}:${utcMinutes}:${utcSeconds}`;
        }
        
        // Если уже в правильном формате
        return dateTimeStr;
      };
      
      const payload = {
        project_id: projectId,
        call_datetime: formatDateTime(formData.call_datetime),
        phone_number: formData.phone_number,
        operator_id: formData.operator_id,
        customer_question: formData.customer_question,
        operator_response: formData.operator_response,
        internal_comment: formData.internal_comment,
        needs_os: formData.needs_os,
        comment_for_send: formData.comment_for_send,
        os_given: false,
        tag_ids: selectedTags.map(tag => tag.id),
        assessment_date: formData.assessment_date ? (() => {
          // Создаем дату из строки datetime-local (в локальном времени)
          const localDate = new Date(formData.assessment_date);
          
          // Конвертируем в UTC, вычитая 3 часа (предполагаем, что локальное время MSK)
          const utcDate = new Date(localDate.getTime() - 3 * 60 * 60 * 1000);
          
          // Форматируем дату в ISO 8601 без миллисекунд
          const year = utcDate.getUTCFullYear();
          const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
          const day = String(utcDate.getUTCDate()).padStart(2, '0');
          const hours = String(utcDate.getUTCHours()).padStart(2, '0');
          const minutes = String(utcDate.getUTCMinutes()).padStart(2, '0');
          const seconds = String(utcDate.getUTCSeconds()).padStart(2, '0');
          
          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        })() : null,
        assessor_name: formData.assessor_name,
        theme: formData.theme,
        subtheme: formData.subtheme
      };

      await api.post('/quality/assessment-customer', {...payload, assessor_id: user.id});

      // Успешное сохранение
      // alert('Оценка от клиента сохранена!');
      
      // Сохраняем ссылку на callback перед закрытием
      const createdCallback = onAssessmentCreated;
      
      // Закрываем форму
      if (onClose) onClose();
      
      // Вызываем callback для обновления списка оценок
      if (createdCallback) {
        createdCallback();
      }
    } catch (err) {
      setError('Не удалось сохранить оценку от клиента. Попробуйте ещё раз.');
      console.error('Ошибка сохранения оценки от клиента:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-content">
        <h3>Загрузка данных...</h3>
        <div>Пожалуйста, подождите</div>
      </div>
    );
  }

  return (
      <div className="modal-content">
        <div className="modal-header">
          <h3>Оценка качества от клиента</h3>
          <button type="button" className="text-modal-close" onClick={onClose} title="Закрыть">
            ×
          </button>
        </div>

        {error && <div className="form-error">{error}</div>}

          <div className="form-group">
          <label htmlFor="assessor_name">ФИО проверяющего *</label>
          <select
              id="assessor_name"
              name="assessor_name"
              value={formData.assessor_name}
              onChange={handleChange}
              required
              style={{width: '100%'}}
          >
            <option value="">-- Выберите ФИО проверяющего --</option>
            <option value="Евстафьева Анастасия">Евстафьева Анастасия</option>
            <option value="Бочкарева Александра">Бочкарева Александра</option>
            <option value="Мальцева Алиса">Мальцева Алиса</option>
            <option value="Соколова Анастасия">Соколова Анастасия</option>
            <option value="Орлов Максим">Орлов Максим</option>
          </select>
          <small>Поле обязательно для заполнения</small>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="call_datetime">Дата и время звонка *</label>
            <input
                type="text"
                id="call_datetime"
                name="call_datetime"
                value={formData.call_datetime}
                onChange={handleChange}
                placeholder="30.01.2026 9:55:55"
                required
            />
            <small>Поле обязательно для заполнения</small>
          </div>

          <div className="form-group">
            <label htmlFor="phone_number">Номер телефона *</label>
            <input
                type="text"
                id="phone_number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                placeholder="79991234567"
                required
            />
            <small>Поле обязательно для заполнения</small>
          </div>

          <div className="form-group">
            <label htmlFor="operator_id">Оператор *</label>
            {fieldOptions.operators.length > 0 ? (
                <select
                    id="operator_id"
                    name="operator_id"
                    value={formData.operator_id}
                    onChange={handleOperatorChange}
                    required
                >
                  <option value="">-- Выберите оператора --</option>
                  {fieldOptions.operators.map(operator => (
                      <option key={operator.id} value={operator.id}>
                        {`${operator.surname} ${operator.name}`}
                      </option>
                  ))}
                </select>
            ) : (
                <input
                    type="text"
                    id="operator_id"
                    name="operator_name"
                    value={formData.operator_name}
                    onChange={handleChange}
                    placeholder="Введите ФИО оператора"
                    required
                />
            )}
            <small>Поле обязательно для заполнения</small>
          </div>

          <div className="form-group">
            <label htmlFor="customer_question">Вопрос клиента *</label>
            <textarea
                id="customer_question"
                name="customer_question"
                rows="3"
                value={formData.customer_question}
                onChange={handleChange}
                placeholder="Введите вопрос клиента"
                required
                style={{width: '100%'}}
            ></textarea>
            <small>Поле обязательно для заполнения</small>
          </div>

          <div className="form-group">
            <label htmlFor="operator_response">Ответ оператора *</label>
            <textarea
                id="operator_response"
                name="operator_response"
                rows="3"
                value={formData.operator_response}
                onChange={handleChange}
                placeholder="Введите ответ оператора"
                required
                style={{width: '100%'}}
            ></textarea>
            <small>Поле обязательно для заполнения</small>
          </div>

          {/* Блок выбора тегов */}
          <div className="form-group">
            <label>Теги ошибок *</label>
            <div className="tags-container">
              {/* Выбранные теги */}
              <div className="selected-tags">
                {selectedTags.map(tag => (
                    <span key={tag.id} className="tag-badge">
                  {tag.name}
                      <button
                          type="button"
                          className="remove-tag"
                          onClick={() => removeTag(tag.id)}
                          aria-label={`Удалить тег ${tag.name}`}
                      >
                    ×
                  </button>
                </span>
                ))}
              </div>
              {/* Выпадающий список для выбора тега */}
              <select
                  onChange={handleTagSelect}
                  disabled={tagsLoading}
                  value=""
                  style={{width: '100%'}}
              >
                <option value="">-- Выберите тег --</option>
                {availableTags.map(tag => (
                    <option key={tag.id} value={tag.id}>
                      {tag.name}
                    </option>
                ))}
              </select>
              {tagsLoading && <span>Загрузка тегов...</span>}
            </div>
            <small>Выберите хотя бы один тег ошибки</small>
          </div>

          <div className="form-group">
            <label htmlFor="assessment_date">Дата и время оценки</label>
            <input
                type="datetime-local"
                id="assessment_date"
                name="assessment_date"
                value={formData.assessment_date}
                onChange={handleChange}
                style={{width: '100%'}}
            />
          </div>

          <div className="form-group">
            <label htmlFor="internal_comment">Внутренний комментарий</label>
            <textarea
                id="internal_comment"
                name="internal_comment"
                rows="3"
                value={formData.internal_comment}
                onChange={handleChange}
                placeholder="Введите внутренний комментарий"
                style={{width: '100%'}}
            ></textarea>
          </div>

          <div className="form-group">
            <label htmlFor="needs_os">Требуется ОС</label>
            <input
                type="checkbox"
                id="needs_os"
                name="needs_os"
                checked={formData.needs_os}
                onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="comment_for_send">Комментарий для отправки оператору</label>
            <textarea
                id="comment_for_send"
                name="comment_for_send"
                rows="3"
                value={formData.comment_for_send}
                onChange={handleChange}
                placeholder="Введите комментарий для отправки оператору"
                style={{width: '100%'}}
            ></textarea>
          </div>
          
          <div className="form-group">
            <label htmlFor="theme">Тема</label>
            {loadingThemes ? (
              <span>Загрузка тем...</span>
            ) : (
              <select
                id="theme"
                name="theme"
                value={formData.theme}
                onChange={handleThemeChange}
                style={{width: '100%'}}
              >
                <option value="">-- Выберите тему --</option>
                {themes.map(theme => (
                  <option key={theme.id} value={theme.name}>
                    {theme.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="subtheme">Подтема</label>
            {subthemes.length === 0 ? (
              <span>{formData.theme ? 'Нет доступных подтем' : 'Выберите тему для отображения подтем'}</span>
            ) : (
              <select
                id="subtheme"
                name="subtheme"
                value={formData.subtheme}
                onChange={handleChange}
                style={{width: '100%'}}
              >
                <option value="">-- Выберите подтему --</option>
                {subthemes.map(subtheme => (
                  <option key={subtheme.id} value={subtheme.name}>
                    {subtheme.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="modal-actions">
            {onClose && (
                <button
                    type="button"
                    className="btn-cancel"
                    onClick={onClose}
                    disabled={submitting}
                >
                  Отмена
                </button>
            )}
            <button
                type="submit"
                className="btn-submit"
                disabled={submitting}
            >
              {submitting ? 'Сохранение...' : 'Сохранить оценку'}
            </button>
          </div>
        </form>
      </div>
  );
};

export default CreateCustomerAssessmentForm;